'use client'
import type { FC } from 'react'
import NextImage from 'next/image'
import styled from 'styled-components'
import { motion } from 'motion/react'
import { defaultGrid, innerGrid, mq, presetsFluid, rgbAlpha, VARIANTS_FADE_FROM_RIGHT_TO_LEFT, vw } from '@/styles'
import type { TypeColors } from '@/styles'
import { useParams } from 'next/navigation'
import type { TypeLang, TypeCmsVectorImage } from '@/schema'
import { Link } from '@/components/base'
import { halfScreenGrid } from './HeaderBrands'

type Props = {
  data: {
    name: string
    description: string
    slug: string
    medium?: TypeCmsVectorImage
  }[]
  bgColor: TypeColors
  color: TypeColors
}

const OUR_BRANDS_TRANSLATIONS = {
  es: {
    title: 'Nuestras marcas'
  },
  en: {
    title: 'Our brands'
  }
}


const oppositeColors:{[key in TypeColors]?:TypeColors} = {
  '--color-white': '--color-blue--dark',
  '--color-blue--dark': '--color-white',
  '--color-black': '--color-white',
  '--color-background': '--color-blue--dark',
  '--color-blue' : '--color-white'
}

export const brandsLogoSizes = {
  cose: { width: 252, height: 132 },
  ladecor: { width: 392, height: 132 },
  softy: { width: 252, height: 132 },
}

const getItems = (data:Props['data']) => data.map((item, _idx) => {
  return (
    <li key={item.slug}>
      <motion.article initial='initial' animate='animate' exit='exit' variants={VARIANTS_FADE_FROM_RIGHT_TO_LEFT(1)}>
        <Link href={`/brands/${item.slug}`} rel='bookmark' aria-label={item.name}>
          <div className='logo'>
            <NextImage src={`/images/svg/logo--${item.slug}.svg`} alt={item.name} width={brandsLogoSizes[item.slug]?.width} height={brandsLogoSizes[item.slug]?.height} unoptimized/>
          </div>
          <h3 className='srt-only'>{item.name}</h3>
          <p>{item.description}</p>
          <div className='icon'>
            <svg xmlns='http://www.w3.org/2000/svg' width='6' height='10' viewBox='0 0 6 10' fill='none'><path d='M.5.5l5 4.5-5 4.5' stroke='currentColor' strokeLinecap='round' strokeLinejoin='round' /></svg>
          </div>
        </Link>
      </motion.article>
    </li>
  )
})

const headingId = 'brands-list'
export const MainBrands:FC<Props> = ({ data, bgColor, color }) => {
  const { lang } = useParams() as { lang: TypeLang }
  const Items = getItems(data)

  return (
    <Main $bgColor={bgColor} $color={color}>
      <Section aria-labelledby={headingId}>
        <h2 id={headingId} className='srt-only'>{OUR_BRANDS_TRANSLATIONS[lang].title}</h2>
        <ul>
          {Items}
        </ul>
      </Section>
    </Main>
  )
}

type MainProps = {
  $bgColor: TypeColors
  $color: TypeColors
}

const Main = styled.main<MainProps>`
  background-color: ${({ $bgColor }) => `var(${$bgColor})`};
  color: ${({ $color }) => `var(${$color})`};
  border-top: 1px solid ${rgbAlpha('--color-white', .2)};

  ${mq.greaterThan('tablet')} {
    border-top: none;
    flex-shrink: 0;
    width: 50%;
  }

  /* a {
    &:hover {
      color: ${({ $color }) => `var(${oppositeColors[$color] ?? '--color-text'})`};
    }
  } */
`

const Section = styled.section`
  width: 100%;

  ul {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    justify-content: stretch;
  }

  a {
    ${innerGrid(defaultGrid.mobile!)};
    border-bottom: 1px solid ${rgbAlpha('--color-white', .2)};
		grid-auto-rows: max-content;
    height: fit-content;
    padding: ${vw(20, 'mobile')};
		opacity: .7;
    position: relative;
    transition: 400ms all ease-out;

    ${mq.greaterThan('nexus7')} {
      padding: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      ${innerGrid(halfScreenGrid.tablet!)};
      padding: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(halfScreenGrid.desktop!)};
      padding: 30px;
    }

    &:hover {
      background-color: ${rgbAlpha('--color-white', .05)};
			opacity: 1;
      /* border-bottom: 1px solid ${rgbAlpha('--color-blue--dark', .2)}; */

      p {
        opacity: 1;
      }

      .logo {
        /* mix-blend-mode: difference; */
        // transform: translate(8px, 8px) scale(1.05);
      }

      .icon {
        transform: translateX(5px);
      }
    }
  }

  .logo {
    align-items: center;
    display: flex;
    grid-column: 1 / span 5;
    grid-row: 1 / span 1;
    height: 40px;
    justify-content: flex-start;
    margin-bottom: 30px;
    transition: 350ms transform ease-out;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 4;
    }

    img {
      height: 100%;
      object-fit: contain;
      transition: 350ms mix-blend-mode ease-out;
      width: auto;
    }
  }

  h3, p {
    grid-column: 1 / span 5;
    grid-row: 2 / span 1;
    opacity: .7;
    transition: 350ms opacity ease-out;
  }

  p {
    ${presetsFluid.font15()}
    text-align: left;
		max-width: ${vw(364, 'mobile')};

		${mq.greaterThan('nexus7')} {
			max-width: ${vw(364, 'tablet')};
		}

		${mq.greaterThan('tablet')} {
			max-width: ${vw(364, 'desktop')};
		}

		${mq.greaterThan('desktop')} {
			${presetsFluid.font15_rel()}
		}
  }

  .icon {
    align-items: center;
    color: inherit;
    display: flex;
    flex-shrink: 0;
    height: 20px;
    position: absolute;
    right: ${vw(20, 'mobile')};
    top: 50%;
    transition: 500ms transform ease-out, 350ms color ease-out;
    width: 12px;

    ${mq.greaterThan('nexus7')} {
      right: ${vw(20, 'tablet')};
    }

    ${mq.greaterThan('tablet')} {
      right: ${vw(30, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      right: 30px;
    }

    svg {
      height: auto;
      width: 12px;
    }
  }
`
